/// <amd-module name="Artifacts/Package/hooks/PackageDetailsResolver" />
import { PackageActions } from "Artifacts/Package/redux/Actions";
import { IPackageDetailsDataState } from "Artifacts/Package/redux/Contracts";
import { getSelectedView } from "Artifacts/Root/helpers/FeedHelpers";
import {
  decodeName,
  decodeVersion,
  encodeName,
  encodeVersion,
} from "Artifacts/Root/helpers/PackageDecoderHelper";
import { useFeed } from "Artifacts/Root/hooks/FeedUrlResolver";
import { getStore } from "Artifacts/Root/Redux";
import { packageDetailsAnyTab } from "Artifacts/Root/routing/Constants";
import { IPackageDetailsContainerRouteParams } from "Artifacts/Root/routing/Types";
import * as React from "react";
import { generatePath, matchPath, useHistory, useLocation, useParams } from "react-router-dom";
import { useComponentContext } from "VSS/Platform/Layout";

/**
 * hook for retrieving details about an existing package
 * @param packageDetailsTabRoute route string for which details tab to redirect to if version changes
 * @returns IFeed for use in requests to the back-end
 */
export function usePackageDetails(packageDetailsTabRoute: string): void {
  const { pageContext } = useComponentContext();
  const { feed } = useFeed();
  // Get routing objects
  const params = useParams<IPackageDetailsContainerRouteParams>();

  let preferRelease = false;
  const urlQuery = new URLSearchParams(window.location.search);
  if (urlQuery.has("preferRelease")) {
    preferRelease = urlQuery.get("preferRelease") === "true";
  }
  const history = useHistory();
  const location = useLocation();
  // Get redux store
  const store = getStore(pageContext);
  const { packageState } = store.getState();
  const pkgData = packageState.data as IPackageDetailsDataState;
  const dispatch = store.dispatch;

  // Resolve IPackageDetailsData from URL params and place in redux store
  React.useEffect(() => {
    if (feed) {
      const {
        packageName: packageNameFromUrl,
        version: versionFromUrl,
        protocol: protocolFromUrl,
      } = params;

      if (
        feed &&
        (!pkgData ||
          !pkgData.pkg ||
          (versionFromUrl &&
            !isSamePackage(
              protocolFromUrl,
              decodeName(packageNameFromUrl, protocolFromUrl),
              decodeVersion(versionFromUrl, protocolFromUrl),
              pkgData,
            )))
      ) {
        const match = matchPath(location.pathname, { path: packageDetailsAnyTab, exact: true });
        const decodedPackageVersion = versionFromUrl
          ? decodeVersion(versionFromUrl, protocolFromUrl)
          : "";
        match &&
          dispatch(
            PackageActions.loadPackageDetails(
              feed.id,
              protocolFromUrl,
              decodeName(packageNameFromUrl, protocolFromUrl),
              (protocol: string, name: string, version: string) => {
                history.replace(
                  generatePath(packageDetailsAnyTab, {
                    ...match?.params,
                    packageName: encodeName(name, protocol),
                    version: encodeVersion(version, protocol),
                  }),
                );
              },
              decodedPackageVersion,
              getSelectedView(feed)?.id,
              feed.project?.name,
              preferRelease,
            ),
          );
      }
    }
  }, [
    dispatch,
    pageContext,
    params,
    history,
    location,
    pkgData,
    feed,
    packageDetailsTabRoute,
    preferRelease,
  ]);
}

function isSamePackage(
  protocol: string,
  packageIdentifier: string,
  version: string,
  pkgData: IPackageDetailsDataState,
): boolean {
  const isSameProtocol = pkgData.pkg?.protocol.toLocaleLowerCase() === protocol.toLocaleLowerCase();
  const isSamePackage =
    pkgData.pkg?.name === packageIdentifier ||
    pkgData.pkg?.id.toLocaleLowerCase() === packageIdentifier.toLocaleLowerCase();
  const isSameVersion =
    pkgData.currentPackageVersion?.version === version ||
    pkgData.currentPackageVersion?.normalizedVersion === version ||
    pkgData.currentPackageVersion?.id.toLocaleLowerCase() === version.toLocaleLowerCase();

  return isSameProtocol && isSamePackage && isSameVersion;
}
